import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '@/hooks/useCart';
import { CartProvider } from '@/hooks/useCart';
import { useUser } from '@/contexts/UserContext';
import axiosInstance from '@/utils/axiosInstance';
import { toast } from 'react-toastify';
import CartItems from '@/components/cart/CartItems';
import OrderSummary from '@/components/cart/OrderSummary';
import DeliveryAddress from '@/components/cart/DeliveryAddress';
import DeliveryTime from '@/components/cart/DeliveryTime';
// import OrderNotes from '@/components/cart/OrderNotes';
import CheckoutHeader from '@/components/cart/CheckoutHeader';
import UpdateAddressModal from '@/components/cart/UpdateAddressModal';
import BottomNav from '@/components/layouts/Footer/BottomNav';
import OrderConfirmation from '../orders/order-confirmation';
import LocalImpactBanner from '../cart/LocalImpactBanner';
import SpecialRequests from '../cart/SpecialRequests';
import OffHoursBanner from '../cart/OffHoursBanner';

const ShoppingCart = () => {
  return (
    <CartProvider>
      <Shopping />
    </CartProvider>
  );
};

const Shopping = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, refreshCart, clearCart } = useCart();
  const { user, refreshUser } = useUser();
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  const [selectedDay, setSelectedDay] = useState(() => {
    return new Date().getHours() > 18 ? 'tomorrow' : 'today';
  });
  const [notes, setNotes] = useState('');
  const [expressDelivery, setExpressDelivery] = useState(false);
  const [selfPickup, setSelfPickup] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddressUpdateModalOpen, setIsAddressUpdateModalOpen] = useState(false);

  // Order confirmation state
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderDetails, setOrderDetails] = useState({
    orderId: '',
    total: 0,
    estimatedDelivery: '',
    vendorName: '',
    vendorContact: '',
  });

  // Get selected vendor info
  const [selectedVendor, setSelectedVendor] = useState(null);

  useEffect(() => {
    const vendorData = JSON.parse(localStorage.getItem('selectedVendor'));
    setSelectedVendor(vendorData);
  }, []);

  const calculateTotal = () => {
    const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const mrpTotal = cart.reduce((sum, item) => sum + item.mrp * item.quantity, 0);
    const discount = mrpTotal - subtotal;
    const platformFees = 0;
    const deliveryCharges = selectedVendor?.deliveryChargeWaived
      ? 0
      : selectedVendor?.deliveryCharge || 0;
    const totalAmount = subtotal + platformFees + deliveryCharges;
    const minimumOrderValue = selectedVendor?.minimumOrder || 0;
    const minimumOrderValueMet = subtotal < minimumOrderValue;

    return {
      subtotal,
      mrpTotal,
      discount,
      platformFees,
      deliveryCharges,
      total: totalAmount,
      minimumOrderValue,
      minimumOrderValueMet,
    };
  };

  const totals = calculateTotal();

  const fetchAddress = async () => {
    const address = user?.addressModel;
    setAddresses(address || []);
    const defaultAddress = address?.find((item) => item.defaultAdd);
    setSelectedAddress(defaultAddress || address?.[0]);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    refreshCart();
  }, []);

  useEffect(() => {
    fetchAddress();
  }, [user]);

  useEffect(() => {
    if (cart.length === 0 && !orderSuccess) {
      navigate('/store');
    }
  }, [cart, orderSuccess]);

  const formatDeliveryTime = (timeSlot, isExpress, day) => {
    if (isExpress) {
      return `Today, within 1 hour`;
    }

    const [start, end] = timeSlot.split('-');
    const formatTime = (time) => {
      const [hours, minutes] = time.split(':');
      const date = new Date(0, 0, 0, hours, minutes);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return `${day === 'today' ? 'Today' : 'Tomorrow'}, ${formatTime(start)} - ${formatTime(end)}`;
  };

  const handleSubmit = async (unitNo) => {
    if (totals.minimumOrderValueMet) {
      toast.info(
        `Minimum order amount must be greater than ₹${totals.minimumOrderValue}. Please add items worth ₹${(totals.minimumOrderValue - totals.subtotal).toFixed(2)} more to place order.`
      );
      return;
    }

    if (!selectedAddress?.unitNo && !unitNo) {
      toast.info('Please enter the House No and try again');
      return;
    }

    if (
      !selectedAddress ||
      !selectedAddress.societyName ||
      !selectedAddress.locality ||
      !selectedAddress.city ||
      !selectedAddress.state ||
      !selectedAddress.country ||
      !selectedAddress.pinCode ||
      !selectedAddress?.contactNo
    ) {
      setIsSubmitting(false);
      toast.error('Address is Required');
      return;
    }

    const orderData = {
      mrp: totals.mrpTotal,
      discount: totals.discount,
      platformFees: totals.platformFees,
      deliveryCharges: totals.deliveryCharges,
      total: totals.total,
      contactNo: selectedAddress?.contactNo,
      deliveryAddress: `${selectedAddress?.unitNo || unitNo}, ${selectedAddress?.societyName}, Plot/Pocket No ${selectedAddress?.plotNo}, Sector ${selectedAddress?.sector}, ${selectedAddress?.locality}, ${selectedAddress?.state}, ${selectedAddress?.district}, ${selectedAddress?.pinCode}, ${selectedAddress?.country}`,
      status: 'Pending',
      deliveryDate:
        selectedDay === 'today'
          ? new Date().toISOString().split('T')[0]
          : new Date(Date.now() + 86400000).toISOString().split('T')[0],
      timeSlot: expressDelivery ? getExpressDeliverySlot() : selectedTimeSlot,
      expressDelivery,
      selfPickup,
      note: notes,
      items: cart.map((item) => ({
        productId: item.id,
        // inventoryId: item.variant.inventoryId,
        productName: item.name,
        quantity: item.quantity,
        productImageUrl: item.image,
        unit: item?.variant?.unit || item?.unit,
        variant: item.variant.unit === 'pc' ? '' : item?.variant?.variant || '',
        updatedVariant: item.variant.unit === 'pc' ? '' : item?.variant?.variant || '',
        mrp: item.mrp,
        netPrice: item.price,
        updatedNetPrice: item.price,
      })),
    };

    console.log(orderData);
    // return;

    if (orderData?.items?.length < 1) {
      toast.error('Cart is Empty! Try adding items to the cart');
      setIsSubmitting(false);
      return;
    }

    const addressId = selectedAddress?.id;

    try {
      setIsSubmitting(true);

      if (!selectedVendor) {
        toast.info('No vendor is selected! Please select a vendor');
        return;
      }

      const response = await axiosInstance.post(
        `${import.meta.env.VITE_API_URL}/rest/subziwale/api/v1/order?addressId=${addressId}`,
        orderData,
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Vendor-Id': selectedVendor.vendorId,
            'X-User-Id': user?.id,
            sessionToken: localStorage.getItem('sessionToken'),
            refreshToken: localStorage.getItem('refreshToken'),
          },
        }
      );

      // toast.success('Order placed successfully!');

      // Format delivery time for display
      const formattedDeliveryTime = formatDeliveryTime(
        selectedTimeSlot,
        expressDelivery,
        selectedDay
      );

      // Set order details for confirmation
      setOrderDetails({
        orderId: response.data?.id || `ORD-${Math.floor(Math.random() * 10000)}`,
        total: Math.ceil(totals.total),
        estimatedDelivery: formattedDeliveryTime,
        vendorName: selectedVendor?.vendorName || '',
        vendorContact: selectedVendor?.contactNo || '',
      });

      // Show confirmation and clear cart
      setOrderSuccess(true);
      clearCart();
    } catch (error) {
      console.error('Error submitting order:', error);
      toast.error(error?.message || 'Failed to place order! Please try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinueShopping = () => {
    navigate('/store');
  };

  const handleViewOrder = (id) => {
    navigate(`/history/${id}`);
  };

  const getExpressDeliverySlot = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const endTime = new Date(now.getTime() + 60 * 60 * 1000);
    const endHour = endTime.getHours();
    const endMinute = endTime.getMinutes();
    return `${currentHour.toString().padStart(2, '0')}:${currentMinute
      .toString()
      .padStart(
        2,
        '0'
      )}-${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`;
  };

  // If order is successful, show the confirmation component
  if (orderSuccess) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <OrderConfirmation
          orderId={orderDetails.orderId}
          total={orderDetails.total}
          estimatedDelivery={orderDetails.estimatedDelivery}
          vendorName={orderDetails?.vendorName}
          vendorContact={orderDetails?.vendorContact}
          onContinueShopping={handleContinueShopping}
          onViewOrder={handleViewOrder}
        />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen pb-14">
      <div className="max-w-md mx-auto bg-white shadow-sm">
        <CheckoutHeader />

        {/* Main Content */}
        <div className="divide-y divide-gray-100">
          {/* Cart Items */}
          <CartItems
            cart={cart}
            updateQuantity={handleUpdateQuantity}
            navigateToStore={() => navigate('/store')}
          />

          {/* Order Summary */}
          <OrderSummary totals={totals} />

          {/* Delivery Address */}
          <DeliveryAddress selectedAddress={selectedAddress} />

          {/* Delivery Time */}
          <DeliveryTime
            selectedDay={selectedDay}
            setSelectedDay={setSelectedDay}
            selectedTimeSlot={selectedTimeSlot}
            setSelectedTimeSlot={setSelectedTimeSlot}
            expressDelivery={expressDelivery}
            setExpressDelivery={setExpressDelivery}
            selfPickup={selfPickup}
            setSelfPickup={setSelfPickup}
          />

          {/* Notes */}
          {/* <OrderNotes notes={notes} setNotes={setNotes} /> */}
          <SpecialRequests vendor={selectedVendor} notes={notes} onNotesChange={setNotes} />
          {selectedVendor?.offHours && (
            <OffHoursBanner offHours={selectedVendor?.offHours} vendor={selectedVendor} />
          )}
        </div>

        {/* Place Order Button */}
        <div className="p-4 pt-2 border-t bg-white sticky bottom-0 shadow-[0_-1px_2px_rgba(0,0,0,0.05)]">
          <button
            onClick={() => {
              !selectedAddress?.unitNo ? setIsAddressUpdateModalOpen(true) : handleSubmit();
            }}
            disabled={isSubmitting || totals?.minimumOrderValueMet}
            className={`w-full py-3 rounded-lg text-white font-medium transition-all transform hover:scale-[1.01] active:scale-[0.99] ${
              isSubmitting || totals?.minimumOrderValueMet
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-gradient-to-r from-green-500 to-emerald-500 shadow-md hover:shadow-lg'
            }`}
          >
            {isSubmitting ? 'Placing Order...' : `Place Order · ₹${Math.ceil(totals.total)}`}
          </button>
          {totals.subtotal < totals.minimumOrderValue && (
            <p className="text-red-500 text-sm mt-2 text-center flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              Please add ₹{(totals.minimumOrderValue - totals.subtotal).toFixed(2)} more to meet the
              minimum order requirement
            </p>
          )}
        </div>
      </div>

      <UpdateAddressModal
        isOpen={isAddressUpdateModalOpen}
        setIsOpen={setIsAddressUpdateModalOpen}
        fetchAddress={fetchAddress}
        handleSubmit={handleSubmit}
      />

      <BottomNav />
    </div>
  );

  // Helper functions
  function handleUpdateQuantity(id, variantId, change) {
    updateQuantity(id, variantId, change);
  }
};

export default ShoppingCart;
